'use client';

import React from 'react';
import Link from 'next/link';
import { Code2, Mic, FileText, Database, ArrowRight, ArrowLeft, Zap, Globe, Lock, Terminal, BookOpen, ExternalLink } from 'lucide-react';
import DeveloperResources from '@/components/ui/DeveloperResources';

export default function DevelopersPortal() {
 return (
 <main className="min-h-screen bg-[#0a0a0a] text-white selection:bg-violet-500/30 overflow-x-hidden">
 {/* Background Ambience */}
 <div className="fixed inset-0 pointer-events-none">
 <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-violet-600/10 rounded-full blur-[128px]"/>
 <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[128px]"/>
 </div>

 {/* Hero Section */}
 <section className="relative z-10 flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
 <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-sm text-violet-200/80 backdrop-blur-md mb-8">
 <Terminal size={14} />
 <span>Developer Portal</span>
 </div>

 <h1 className="text-4xl md:text-6xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-b from-white to-white/60 pb-4 mb-4">
 Build with EngageIQ
 </h1>

 <p className="max-w-2xl mx-auto text-lg text-zinc-400 leading-relaxed mb-12">
 Powerful APIs for public safety applications. Real-time voice, AI analysis, document generation, and more.
 </p>

 <div className="flex flex-col sm:flex-row gap-4">
 <a
 href="https://docs.neutralaiz.com"
 target="_blank"
 rel="noopener noreferrer"
 className="px-6 py-3 bg-violet-600 hover:bg-violet-500 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2"
 >
 <BookOpen size={18} />
 View Documentation
 <ExternalLink size={14} />
 </a>
 </div>
 </section>

 {/* API Cards */}
 <section className="relative py-24 px-4 bg-zinc-900/30 border-t border-white/5">
 <div className="">
 <div className="text-center mb-16 space-y-4">
 <h2 className="text-3xl md:text-4xl font-bold">Core APIs</h2>
 <p className="text-zinc-400 max-w-2xl mx-auto">
 Integrate EngageIQ capabilities into your applications with our comprehensive API suite.
 </p>
 </div>

 <div className="grid md:grid-cols-3 gap-6">
 <ApiCard
 icon={<Mic className="text-blue-400"size={32} />}
 title="LiveKit API"
 description="Real-time voice and video communication with transcription, WebRTC-powered conferencing, and low-latency streaming."
 features={['Voice/Video Calls', 'Real-time Transcription', 'Screen Sharing', 'Recording']}
 accentColor="blue"
 />
 <ApiCard
 icon={<Zap className="text-violet-400"size={32} />}
 title="AI Engine SDK"
 description="Intelligent analysis, entity extraction, and agentic workflows. Process transcripts and generate insights automatically."
 features={['Entity Extraction', 'Sentiment Analysis', 'Smart Suggestions', 'Agentic Chains']}
 accentColor="violet"
 />
 <ApiCard
 icon={<FileText className="text-emerald-400"size={32} />}
 title="Reports API"
 description="Automated document generation from call transcripts. Create incident reports, summaries, and formatted documents."
 features={['Template System', 'PDF Generation', 'DOCX Export', 'Custom Branding']}
 accentColor="emerald"
 />
 </div>
 </div>
 </section>

 {/* Developer Resources */}
 <section className="relative py-24 px-4 border-t border-white/5">
 <div className="">
 <DeveloperResources />
 </div>
 </section>

 {/* Integration Options */}
 <section className="relative py-24 px-4">
 <div className="">
 <div className="text-center mb-16 space-y-4">
 <h2 className="text-3xl md:text-4xl font-bold">Integration Options</h2>
 <p className="text-zinc-400 max-w-2xl mx-auto">
 Multiple ways to connect your systems with EngageIQ.
 </p>
 </div>

 <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
 <IntegrationCard
 icon={<Globe className="text-cyan-400"size={24} />}
 title="REST APIs"
 description="Standard HTTP endpoints for all platform features"
 />
 <IntegrationCard
 icon={<Zap className="text-yellow-400"size={24} />}
 title="Webhooks"
 description="Real-time event notifications to your endpoints"
 />
 <IntegrationCard
 icon={<Database className="text-green-400"size={24} />}
 title="Data Streams"
 description="Live transcript and analysis data via WebSocket"
 />
 <IntegrationCard
 icon={<Lock className="text-blue-400"size={24} />}
 title="OAuth 2.0"
 description="Secure authentication and authorization"
 />
 </div>
 </div>
 </section>

 {/* Quick Start */}
 <section className="relative py-24 px-4 bg-zinc-900/30 border-t border-white/5">
 <div className="">
 <div className="text-center mb-12 space-y-4">
 <h2 className="text-3xl md:text-4xl font-bold">Quick Start</h2>
 <p className="text-zinc-400">Get up and running in minutes</p>
 </div>

 <div className="p-6 rounded-2xl bg-black/50 border border-white/10 font-mono text-sm">
 <div className="flex items-center gap-2 mb-4 text-zinc-500">
 <Code2 size={16} />
 <span>Initialize the client</span>
 </div>
 <pre className="text-blue-400 overflow-x-auto">
{`import { AIEngineClient } from '@neutralaiz/aiengine';

const client = new AIEngineClient({
 apiKey: process.env.AIENGINE_API_KEY,
});

// Analyze a transcript
const result = await client.analyze.transcript({
 transcript:"Caller: I need to report an incident...",
 callId:"call-123",
});

console.log(result.analysis);`}
 </pre>
 </div>

 <div className="mt-8 text-center">
 <a
 href="https://docs.neutralaiz.com/quickstart"
 target="_blank"
 rel="noopener noreferrer"
 className="inline-flex items-center gap-2 text-violet-400 hover:text-violet-300 transition-colors"
 >
 View full quickstart guide
 <ArrowRight size={16} />
 </a>
 </div>
 </div>
 </section>

 </main>
 );
}

function ApiCard({
 icon,
 title,
 description,
 features,
 accentColor
}: {
 icon: React.ReactNode;
 title: string;
 description: string;
 features: string[];
 accentColor: 'blue' | 'violet' | 'emerald';
}) {
 const borderColors = {
 blue: 'hover:border-blue-500/50',
 violet: 'hover:border-violet-500/50',
 emerald: 'hover:border-emerald-500/50',
 };

 const dotColors = {
 blue: 'bg-blue-500',
 violet: 'bg-violet-500',
 emerald: 'bg-emerald-500',
 };

 return (
 <div className={`p-8 rounded-2xl bg-white/5 border border-white/5 ${borderColors[accentColor]} hover:bg-white/10 transition-all duration-300 backdrop-blur-sm group`}>
 <div className="mb-6 bg-white/5 w-16 h-16 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
 {icon}
 </div>
 <h3 className="text-xl font-semibold mb-3 text-white">{title}</h3>
 <p className="text-zinc-400 leading-relaxed mb-6">
 {description}
 </p>
 <ul className="space-y-2">
 {features.map((feature, index) => (
 <li key={index} className="flex items-center gap-2 text-sm text-zinc-300">
 <div className={`w-1.5 h-1.5 rounded-full ${dotColors[accentColor]}`} />
 {feature}
 </li>
 ))}
 </ul>
 </div>
 );
}

function IntegrationCard({
 icon,
 title,
 description
}: {
 icon: React.ReactNode;
 title: string;
 description: string;
}) {
 return (
 <div className="p-6 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 hover:bg-white/10 transition-all duration-300">
 <div className="mb-4">
 {icon}
 </div>
 <h3 className="text-lg font-semibold mb-2">{title}</h3>
 <p className="text-sm text-zinc-400">{description}</p>
 </div>
 );
}
